// ═══════════════════════════════════════════════════════
// HOOKS — shared React hooks
// Uses design token breakpoints from theme.js
// ═══════════════════════════════════════════════════════
import { useState, useEffect, useRef } from "react";
import { flattenNav, PERFIL_NAV, type NavItem } from "./navConfig.js";
import { BP } from "./theme.js";
export { useQuery } from "../useQuery.js";

// ── Type Interfaces ──────────────────────────────────

export interface GridStyle {
  display: "grid";
  gridTemplateColumns: string;
  gap: number;
  marginBottom: number;
}

/**
 * Returns true if window width is below the given breakpoint.
 * Uses BP.mobile (640px) by default. Updates on resize.
 */
export function useIsMobile(breakpoint: number = BP.mobile): boolean {
  const [mobile, setMobile] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const handler = (): void => setMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return mobile;
}

/**
 * Returns true if window width is below the tablet breakpoint (1024px).
 */
export function useIsTablet(breakpoint: number = BP.tablet): boolean {
  const [tablet, setTablet] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const handler = (): void => setTablet(window.innerWidth < breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return tablet;
}

/**
 * Responsive CSS grid helper.
 * - mobile (< BP.mobile): 1 column
 * - tablet (< BP.tablet): min(cols, 2)
 * - desktop: N columns
 */
export function rGrid(cols: number, gap: number = 16, mb: number = 20): GridStyle {
  if (typeof window === "undefined") {
    return { display: "grid", gridTemplateColumns: `repeat(${cols},1fr)`, gap, marginBottom: mb };
  }
  const w = window.innerWidth;
  if (w < BP.mobile) {
    return { display: "grid", gridTemplateColumns: "1fr", gap, marginBottom: mb };
  }
  if (w < BP.tablet) {
    const tabCols = Math.min(cols, 2);
    return {
      display: "grid",
      gridTemplateColumns: `repeat(${tabCols},1fr)`,
      gap,
      marginBottom: mb,
    };
  }
  return { display: "grid", gridTemplateColumns: `repeat(${cols},1fr)`, gap, marginBottom: mb };
}

/**
 * Sets document.title based on page ID.
 * Maps pageId to Portuguese labels via NAV config.
 * Format: "LPA Wealth | {page label}"
 */
// ═══════════════════════════════════════════════════════
// MODAL A11Y — focus trap, ESC handler, focus restore
// ═══════════════════════════════════════════════════════
const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Provides modal accessibility: focus trap, ESC-to-close, focus restore.
 * Attach the returned ref to the modal's outermost container.
 */
export function useModalA11y(onClose: () => void): React.RefObject<HTMLDivElement | null> {
  const trapRef = useRef<HTMLDivElement | null>(null);
  const prevFocus = useRef<Element | null>(null);

  // Save previous focus and auto-focus first element on mount
  useEffect(() => {
    prevFocus.current = document.activeElement;
    const el = trapRef.current;
    if (el) {
      const first = el.querySelector<HTMLElement>(FOCUSABLE);
      if (first) first.focus();
    }
    return () => {
      if (prevFocus.current && "focus" in prevFocus.current) {
        (prevFocus.current as HTMLElement).focus();
      }
    };
  }, []);

  // ESC handler + Tab focus trap
  useEffect(() => {
    const el = trapRef.current;
    if (!el) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    el.addEventListener("keydown", handler);
    return () => el.removeEventListener("keydown", handler);
  }, [onClose]);

  return trapRef;
}

// ═══════════════════════════════════════════════════════
// PAGE TITLE
// ═══════════════════════════════════════════════════════
export function usePageTitle(pageId?: string): void {
  useEffect(() => {
    if (!pageId) return;
    const allPages: NavItem[] = [...flattenNav(), PERFIL_NAV];
    const navItem = allPages.find((n) => n.id === pageId);
    const pageLabel = navItem?.label || "Dashboard";
    document.title = `LPA Wealth | ${pageLabel}`;
    return () => {
      document.title = "LPA Wealth | Portal do Investidor";
    };
  }, [pageId]);
}
